import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, ConfirmDialog, Drawer, FileUpload, Skeleton, Textarea, useToast } from '@dreamtree-org/twreact-ui';
import { BookOpen, CheckCircle2, Download, Eye, History, PowerOff, Rocket, Save } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { errorText, formatDate } from '@/lib/format';
import { usePhone } from '@/lib/useMediaQuery';
import { problemRows, positionLabel, failureRows, homeState, byteSize, MAX_TEMPLATE_BYTES } from '../../lib/customHome';

/**
 * Custom HTML home (ADR 0007): the store's own HTML page at `/`, with
 * placeholders, under a strict content security policy. Upload or paste,
 * check, save as a draft, preview, publish, roll back or turn it off.
 * The server validates everything again; this screen only shows its answers.
 * Shown only to staff holding store:custom_code (the server is the boundary).
 */
export default function CustomHomeStudio() {
  const { fetchApi } = useAppApi();
  const { toast } = useToast();
  const phone = usePhone();
  const [state, setState] = useState(null); // { draft, live }
  const [html, setHtml] = useState('');
  const [files, setFiles] = useState([]);
  const [problems, setProblems] = useState(null); // null = not checked
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState('');
  const [loadError, setLoadError] = useState('');
  const [reference, setReference] = useState(null);
  const [refOpen, setRefOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [confirm, setConfirm] = useState(''); // 'publish' | 'disable'

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const r = await fetchApi('customHomeDraft', {}, false);
      setState(r.data);
      const open = r.data.draft || r.data.live;
      setHtml(open ? open.html : '');
    } catch (ex) {
      setLoadError(errorText(ex));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const getReference = async () => {
    if (reference) return reference;
    const r = await fetchApi('customHomeReference', {}, false);
    setReference(r.data);
    return r.data;
  };

  const run = async (key, fn) => {
    setBusy(key);
    try {
      return await fn();
    } catch (ex) {
      setProblems(failureRows(ex));
      return null;
    } finally {
      setBusy('');
    }
  };

  const check = () => run('check', async () => {
    const r = await fetchApi('customHomeValidate', { html }, false);
    setProblems(problemRows(r.data.errors));
    setWarnings(problemRows(r.data.warnings));
    if (r.data.ok) toast.success('No problems found', `${r.data.script_hashes.length} inline script(s) will be allowed by their hash.`);
  });

  const save = () => run('save', async () => {
    const lock = state && state.draft ? state.draft.lock_version : undefined;
    const r = await fetchApi('customHomeSave', { html, lock_version: lock }, false);
    setProblems([]);
    setWarnings(problemRows(r.data.warnings));
    toast.success(`Draft saved (version ${r.data.version})`, 'Preview it, then publish when you are happy.');
    await load();
    return r;
  });

  const preview = () => run('preview', async () => {
    if (!state || !state.draft || state.draft.html !== html) {
      if (!(await save())) return;
    }
    const r = await fetchApi('customHomePreviewLink', {}, false);
    window.open(r.data.path, '_blank', 'noopener');
  });

  const publish = () => run('publish', async () => {
    setConfirm('');
    if (!state || !state.draft || state.draft.html !== html) {
      if (!(await save())) return;
    }
    const r = await fetchApi('customHomePublish', {}, false);
    toast.success(`Version ${r.data.version} is your home page`, 'Shoppers opening your store now see it.');
    await load();
  });

  const disable = () => run('disable', async () => {
    setConfirm('');
    await fetchApi('customHomeDisable', {}, false);
    toast.success('Custom home turned off', 'Shoppers see your standard home page again.');
    await load();
  });

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistory(null);
    try {
      setHistory((await fetchApi('customHomeHistory', {}, false)).data);
    } catch (ex) {
      setHistory([]);
      toast.error('Could not load history', errorText(ex));
    }
  };
  const rollback = (version) => run(`rb${version}`, async () => {
    const r = await fetchApi('customHomeRollback', { version }, false);
    toast.success(`Version ${version} is live again`, `Published as version ${r.data.version}.`);
    setHistory((await fetchApi('customHomeHistory', {}, false)).data);
    await load();
  });

  const pickFile = async (picked) => {
    setFiles(picked);
    const f = picked && picked[0];
    if (!f) return;
    if (f.size > MAX_TEMPLATE_BYTES) {
      setProblems([{ line: null, column: null, code: 'TOO_LARGE', message: `The file is ${Math.ceil(f.size / 1024)} KB; the limit is ${MAX_TEMPLATE_BYTES / 1024} KB.` }]);
      return;
    }
    setHtml(await f.text());
    setProblems(null);
  };

  const downloadStarter = async () => {
    try {
      const ref = await getReference();
      const url = URL.createObjectURL(new Blob([ref.starter_template], { type: 'text/html' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'storefront-home-starter.html';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      toast.error('Could not get the starter template', errorText(ex));
    }
  };
  const openReference = async () => {
    setRefOpen(true);
    try {
      await getReference();
    } catch (ex) {
      toast.error('Could not load the reference', errorText(ex));
    }
  };

  if (loadError) return <Alert variant="error" title="The custom home could not load">{loadError}</Alert>;
  if (!state) return <Skeleton height={360} />;

  const status = homeState(state);
  const size = byteSize(html);

  return (
    <div className="space-y-4">
      <Alert variant="warning" title="Your own code runs on your store">
        This page is served at your store address with your own HTML and scripts, under a strict content security policy: scripts load only from the allowlisted CDNs and cannot call your store&apos;s API. Every change is recorded, and the platform can switch custom homes off.
      </Alert>

      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={status.variant} aria-live="polite">{status.label}</Badge>
          <span className="mr-auto text-xs opacity-60">{Math.ceil(size / 1024)} KB of {MAX_TEMPLATE_BYTES / 1024} KB</span>
          <Button variant="outline" size="sm" leftIcon={<BookOpen className="h-4 w-4" />} onClick={openReference}>Placeholders</Button>
          <Button variant="outline" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={downloadStarter}>Starter template</Button>
          <Button variant="outline" size="sm" leftIcon={<History className="h-4 w-4" />} onClick={openHistory}>History</Button>
        </div>
        <FileUpload accept=".html,.htm,text/html" files={files} onFileSelect={pickFile} onFileRemove={() => setFiles([])} maxSize={MAX_TEMPLATE_BYTES} />
        <Textarea
          label="HTML template"
          helperText="Placeholders like {{store.name}} and {{#featured}}…{{/featured}} are filled on the server; see Placeholders."
          rows={18}
          value={html}
          onChange={(e) => {
            setHtml(e.target.value);
            setProblems(null);
          }}
          className="font-mono text-xs"
          spellCheck={false}
        />
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" leftIcon={<CheckCircle2 className="h-4 w-4" />} loading={busy === 'check'} disabled={!html.trim() || Boolean(busy)} onClick={check}>Check</Button>
          <Button variant="outline" leftIcon={<Save className="h-4 w-4" />} loading={busy === 'save'} disabled={!html.trim() || Boolean(busy)} onClick={save}>Save draft</Button>
          <Button variant="outline" leftIcon={<Eye className="h-4 w-4" />} loading={busy === 'preview'} disabled={!html.trim() || Boolean(busy)} onClick={preview}>Preview</Button>
          <Button leftIcon={<Rocket className="h-4 w-4" />} loading={busy === 'publish'} disabled={!html.trim() || Boolean(busy)} onClick={() => setConfirm('publish')}>Publish</Button>
          {state.live ? (
            <Button variant="outline" leftIcon={<PowerOff className="h-4 w-4" />} loading={busy === 'disable'} disabled={Boolean(busy)} onClick={() => setConfirm('disable')}>Turn off</Button>
          ) : null}
        </div>
      </Card>

      {problems && problems.length ? (
        <Alert variant="error" title={`${problems.length} problem${problems.length === 1 ? '' : 's'} to fix`}>
          <ul className="list-disc space-y-1 pl-5" data-testid="custom-home-errors">
            {problems.map((p, i) => <li key={`${p.line}:${p.column}:${i}`}><strong>{positionLabel(p)}</strong>{positionLabel(p) ? ': ' : ''}{p.message}</li>)}
          </ul>
        </Alert>
      ) : null}
      {problems && !problems.length ? <Alert variant="success" title="No problems found" /> : null}
      {warnings.length ? (
        <Alert variant="warning" title="Worth a look">
          <ul className="list-disc space-y-1 pl-5">{warnings.map((p, i) => <li key={`w${i}`}><strong>{positionLabel(p)}</strong>{positionLabel(p) ? ': ' : ''}{p.message}</li>)}</ul>
        </Alert>
      ) : null}

      <ConfirmDialog
        isOpen={confirm === 'publish'}
        title="Publish your custom home?"
        description="Shoppers opening your store address will see this page straight away. Product, cart and checkout pages are not affected. You can roll back or turn it off at any time."
        confirmLabel="Publish"
        loading={busy === 'publish'}
        onCancel={() => setConfirm('')}
        onConfirm={publish}
      />
      <ConfirmDialog
        isOpen={confirm === 'disable'}
        title="Turn off the custom home?"
        description="Shoppers will see your standard home page again. The version stays in History."
        confirmLabel="Turn off"
        loading={busy === 'disable'}
        onCancel={() => setConfirm('')}
        onConfirm={disable}
      />

      <Drawer isOpen={refOpen} onClose={() => setRefOpen(false)} title="Placeholders" description="Names you can use in the template. Values are escaped for where they appear." placement={phone ? 'bottom' : 'right'} size={phone ? 'lg' : 'md'} swipeToClose>
        {!reference ? <Skeleton count={6} /> : <ReferenceList reference={reference} />}
      </Drawer>

      <Drawer isOpen={historyOpen} onClose={() => setHistoryOpen(false)} title="Custom home versions" description="Put an earlier version live again." placement={phone ? 'bottom' : 'right'} size={phone ? 'lg' : 'md'} swipeToClose>
        {!history ? <Skeleton count={4} /> : history.length ? (
          <ul className="divide-y divide-[var(--app-border)]">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <div className="text-sm font-medium">
                    Version {h.version} {h.status === 'published' ? <Badge variant="success" size="sm">Live</Badge> : h.status === 'draft' ? <Badge size="sm">Draft</Badge> : null}
                  </div>
                  <div className="text-xs opacity-60">{h.published_at ? formatDate(h.published_at) : 'not published'} · {Math.ceil((h.size_bytes || 0) / 1024)} KB · {(h.script_hashes || []).length} inline script(s)</div>
                </div>
                {h.status === 'archived' ? <Button size="sm" variant="outline" loading={busy === `rb${h.version}`} onClick={() => rollback(h.version)}>Make live</Button> : null}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm opacity-70">Nothing saved yet.</p>}
      </Drawer>
    </div>
  );
}

function ReferenceList({ reference }) {
  return (
    <div className="space-y-4 text-sm">
      <section>
        <h3 className="mb-1 font-semibold">Syntax</h3>
        <ul className="space-y-1">{Object.entries(reference.syntax).map(([k, v]) => <li key={k}><code className="text-xs">{k}</code> {v}</li>)}</ul>
      </section>
      <section>
        <h3 className="mb-1 font-semibold">Values</h3>
        <ul className="space-y-1">{[...reference.names, ...reference.patterns].map((n) => <li key={n.name}><code className="text-xs">{`{{${n.name}}}`}</code> <Badge size="xs">{n.type}</Badge> {n.help}</li>)}</ul>
      </section>
      {reference.sections.map((s) => (
        <section key={s.name}>
          <h3 className="mb-1 font-semibold"><code className="text-xs">{`{{#${s.name}}}…{{/${s.name}}}`}</code></h3>
          <p className="mb-1 opacity-70">{s.help}</p>
          <ul className="space-y-1">{s.fields.map((f) => <li key={f.name}><code className="text-xs">{`{{${f.name}}}`}</code> <Badge size="xs">{f.type}</Badge> {f.help}</li>)}</ul>
        </section>
      ))}
      <section>
        <h3 className="mb-1 font-semibold">Page rules</h3>
        <ul className="list-disc space-y-1 pl-5">{reference.rules.map((r) => <li key={r}>{r}</li>)}</ul>
      </section>
      <section>
        <h3 className="mb-1 font-semibold">Content security policy</h3>
        <code className="block whitespace-pre-wrap break-all text-xs">{reference.csp}</code>
      </section>
    </div>
  );
}
